/*
 * internal-video — self-hosted <video> custom element (shared).
 * - data-autoplay="true": muted autoplay while in viewport (lazy: swaps
 *   <source data-src> -> src on first play). Respects prefers-reduced-motion.
 * - data-lazy="true": hydrates a <template> without active sources; loads
 *   metadata within 200px of the viewport or on native-control interaction.
 * - Play button (.internal-video__play) toggles play/pause.
 * - Optional .internal-video__sound-btn (mute), .internal-video__fullscreen-btn,
 *   .internal-video__timeline (progress + click-to-seek).
 * Theme-editor safe: connect/disconnect lifecycle; observer cleaned up.
 */
if (!customElements.get('internal-video')) {
  customElements.define(
    'internal-video',
    class InternalVideo extends HTMLElement {
      connectedCallback() {
        this.lazy = this.dataset.lazy === 'true';
        if (this.lazy) this.querySelector('noscript')?.remove();
        const template = this.lazy && this.querySelector('template');
        if (template) {
          const content = template.content.cloneNode(true);
          for (const source of content.querySelectorAll('source[src]')) {
            source.dataset.src = source.getAttribute('src');
            source.removeAttribute('src');
          }
          template.replaceWith(content);
        }

        this.video = this.querySelector('video');
        if (!this.video) return;

        this.autoplay = this.dataset.autoplay === 'true';
        this._reduce = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;
        this._loaded = !this.video.querySelector('source[data-src]');

        this.playBtn = this.querySelector('.internal-video__play');
        this.soundBtn = this.querySelector('.internal-video__sound-btn');
        this.fsBtn = this.querySelector('.internal-video__fullscreen-btn');
        this.timeline = this.querySelector('.internal-video__timeline');

        this._onPlayClick = this._togglePlay.bind(this);
        this._onSound = this._toggleSound.bind(this);
        this._onFs = this._toggleFullscreen.bind(this);
        this._onTime = this._updateProgress.bind(this);
        this._onSeek = this._seek.bind(this);
        this._onLoad = this._lazyLoad.bind(this);

        if (this.playBtn) this.playBtn.addEventListener('click', this._onPlayClick);
        if (this.soundBtn) this.soundBtn.addEventListener('click', this._onSound);
        if (this.fsBtn) this.fsBtn.addEventListener('click', this._onFs);
        if (this.timeline) {
          this.video.addEventListener('timeupdate', this._onTime);
          this.timeline.addEventListener('click', this._onSeek);
        }

        if (this.lazy) {
          this.video.addEventListener('pointerdown', this._onLoad);
          this.video.addEventListener('keydown', this._onLoad);
          this._observeLoad();
        }

        if (this.autoplay && !this._reduce) {
          this.classList.add('internal-video--muted');
          this.video.muted = true;
          this._observe();
        }
      }

      disconnectedCallback() {
        if (this._io) this._io.disconnect();
        if (this._loadIo) this._loadIo.disconnect();
        if (this.playBtn) this.playBtn.removeEventListener('click', this._onPlayClick);
        if (this.soundBtn) this.soundBtn.removeEventListener('click', this._onSound);
        if (this.fsBtn) this.fsBtn.removeEventListener('click', this._onFs);
        if (this.timeline) {
          this.video.removeEventListener('timeupdate', this._onTime);
          this.timeline.removeEventListener('click', this._onSeek);
        }
        if (this.video) {
          this.video.removeEventListener('pointerdown', this._onLoad);
          this.video.removeEventListener('keydown', this._onLoad);
        }
        this._stop();
      }

      _lazyLoad() {
        if (this._loaded) return;
        const sources = this.video.querySelectorAll('source[data-src]');
        if (sources.length) {
          if (this.lazy) this.video.preload = 'metadata';
          for (const source of sources) {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
          }
          this.video.load();
        }
        this._loaded = true;
        if (this._loadIo) this._loadIo.disconnect();
      }

      _observeLoad() {
        if (this._loaded) return;
        if (!('IntersectionObserver' in window)) {
          this._lazyLoad();
          return;
        }
        this._loadIo = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this._lazyLoad();
          },
          { rootMargin: '200px', threshold: 0 }
        );
        this._loadIo.observe(this);
      }

      _play() {
        this._lazyLoad();
        var p = this.video.play();
        if (p && p.catch) p.catch(function () {});
        this.classList.add('internal-video--playing');
      }
      _stop() {
        if (this.video) this.video.pause();
        this.classList.remove('internal-video--playing');
      }
      _togglePlay() {
        if (this.video.paused) this._play();
        else this._stop();
      }

      _toggleSound() {
        this.video.muted = !this.video.muted;
        this.classList.toggle('internal-video--muted', this.video.muted);
      }

      _toggleFullscreen() {
        if (document.fullscreenElement) {
          document.exitFullscreen();
          this.classList.remove('internal-video--fullscreen');
        } else if (this.requestFullscreen) {
          this.requestFullscreen();
          this.classList.add('internal-video--fullscreen');
        }
      }

      _updateProgress() {
        if (!this.video.duration) return;
        var pct = (this.video.currentTime / this.video.duration) * 100;
        this.timeline.style.setProperty('--completed', pct + '%');
      }
      _seek(e) {
        if (!this.video.duration) return;
        var rect = this.timeline.getBoundingClientRect();
        this.video.currentTime =
          ((e.clientX - rect.left) / rect.width) * this.video.duration;
      }

      /* Muted autoplay only while visible. */
      _observe() {
        if (!('IntersectionObserver' in window)) return;
        var self = this;
        this._io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) self._play();
              else self._stop();
            });
          },
          { threshold: 0.25 }
        );
        this._io.observe(this);
      }
    }
  );
}
